import React from "react";
import { Box, Grid, InputLabel } from "@mui/material";

import { CustomInput } from "../../../../../../components/Custom/Input";
import styles from "../../sessions.module.css";
import type { ErrorRenderer } from "./positionForm.errors";
import type { SessionDates, ShiftTimeInfo } from "./positionForm.types";
import { LabelWithTbdToggle, SessionDateSelect, ShiftTimePicker } from "./PositionFormFields";
import {
  toPickerValue,
  withTbd,
  withTimeIn,
  withTimeOut,
} from "./positionForm.utils";

/** The shift being edited: a `positionTimeInfo` row, or the edit form values. */
type Shift = Partial<ShiftTimeInfo>;
type ShiftUpdater = <T extends Shift>(shift: T) => T;
type CellWidth = { width: string };

type ShiftTimeCellsProps = {
  shift: Shift;
  /** Formik field prefix, e.g. `positionTimeInfo[2].` ("" for the edit form). */
  namePrefix?: string;
  session: SessionDates;
  /** Applies an update to this shift (and to linked rows, when there are any). */
  onShiftChange: (update: ShiftUpdater) => void;
  /** Date changes always target this shift only. */
  onDateChange: (dateKey: string) => void;
  onBlur?: React.FocusEventHandler;
  errorFor: ErrorRenderer;
  widths: { date: CellWidth; time: CellWidth; hours: CellWidth };
  dateLabel?: string;
  showDateLabel?: boolean;
  showTimeEndLabel?: boolean;
  /** Replaces the date dropdown (used for the "whole session" summary row). */
  dateContent?: React.ReactNode;
  wideTimePicker?: boolean;
  /** Overrides the "No. of hours" handler (the edit form uses Formik's handleChange). */
  onHoursChange?: React.ChangeEventHandler<HTMLInputElement>;
};

const fullWidthOnMobile = { "@media (max-width: 600px)": { width: "100%" } };

/**
 * Date / Time In / Time End cells of a shift, or Date / No. of hours when the
 * shift is TBD. Shared by the per-date rows of the add flow and by the
 * single-shift edit form, so both behave exactly the same.
 */
const ShiftTimeCells = ({
  shift,
  namePrefix = "",
  session,
  onShiftChange,
  onDateChange,
  onBlur,
  errorFor,
  widths,
  dateLabel = "Date",
  showDateLabel = true,
  showTimeEndLabel = true,
  dateContent,
  wideTimePicker = false,
  onHoursChange,
}: ShiftTimeCellsProps) => {
  const isTbd = !!shift?.tbd;
  const toggleTbd = () => onShiftChange((current) => withTbd(current, !isTbd));

  return (
    <>
      <Grid item sx={{ ...widths.date, ...fullWidthOnMobile }}>
        <Box className={styles["form-box"]}>
          <InputLabel sx={{ visibility: showDateLabel ? "visible" : "hidden" }}>
            {dateLabel}
          </InputLabel>
          {dateContent ?? (
            <SessionDateSelect
              name={`${namePrefix}dateStart`}
              session={session}
              value={shift?.dateStart}
              onChange={onDateChange}
              onBlur={onBlur}
            />
          )}
          {errorFor("dateStart")}
        </Box>
      </Grid>

      {!isTbd ? (
        <>
          <Grid item sx={{ ...widths.time, ...fullWidthOnMobile }}>
            <Box className={styles["form-box"]} sx={{ alignItems: "center" }}>
              <LabelWithTbdToggle label="Time In" isTbd={isTbd} onToggle={toggleTbd} />
              <ShiftTimePicker
                name={`${namePrefix}timeIn`}
                wide={wideTimePicker}
                value={toPickerValue(shift?.dateStart, shift?.timeIn)}
                onChange={(picked) =>
                  onShiftChange((current) => withTimeIn(current, picked, session?.sessionStart))
                }
              />
              {errorFor("timeIn")}
            </Box>
          </Grid>

          <Grid item sx={{ ...widths.time, ...fullWidthOnMobile }}>
            <Box className={styles["form-box"]}>
              <InputLabel sx={{ visibility: showTimeEndLabel ? "visible" : "hidden" }}>
                Time End
              </InputLabel>
              <ShiftTimePicker
                name={`${namePrefix}timeOut`}
                wide={wideTimePicker}
                value={toPickerValue(shift?.dateStart, shift?.timeOut)}
                onChange={(picked) => {
                  const endHour = picked?.hour();
                  const minute = picked?.minute();
                  onShiftChange((current) =>
                    withTimeOut(current, endHour, minute, session?.sessionStart),
                  );
                }}
              />
              {errorFor("timeOut")}
            </Box>
          </Grid>
        </>
      ) : (
        <Grid item sx={{ ...widths.hours, ...fullWidthOnMobile }}>
          <Box className={styles["form-box"]}>
            <LabelWithTbdToggle label="No. of hours" isTbd={isTbd} onToggle={toggleTbd} />
            <CustomInput
              type="number"
              fullWidth
              placeholder="10"
              name={`${namePrefix}numberOfHours`}
              value={shift?.numberOfHours}
              onBlur={onBlur}
              onChange={
                onHoursChange ??
                ((e) => {
                  const numberOfHours = e?.target?.value;
                  onShiftChange((current) => ({ ...current, numberOfHours }));
                })
              }
            />
            {errorFor("numberOfHours")}
          </Box>
        </Grid>
      )}
    </>
  );
};

export default ShiftTimeCells;
